import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  Collapse,
  IconButton,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Chip,
  Snackbar,
  TextField,
  InputAdornment,
  CircularProgress,
  Grid,
  Tab,
  Tabs,
  Card,
  CardContent
} from '@mui/material';
import { 
  MessageCircle, 
  Mail, 
  UserPlus, 
  Share2, 
  FileText,
  ChevronRight,
  Car,
  Users,
  X,
  ChevronDown,
  FileCode,
  Check,
  Plus,
  Search,
  RefreshCcw,
  UserRound,
  ArrowLeft,
  CheckCircle,
  Copy,
  Info,
  Tag,
  User
} from 'lucide-react';
import axios from 'axios';

// Import client and car result components
const API_BASE_URL = process.env.REACT_APP_DOMAIN ? `${process.env.REACT_APP_DOMAIN}/car_stock/api` : '/car_stock/api';
const CRM_API_URL = process.env.REACT_APP_DOMAIN ? `${process.env.REACT_APP_DOMAIN}/crm/api` : '/crm/api';

const primaryGoals = [
  { id: 'info', icon: <MessageCircle />, label: 'Richiesta informazioni' },
  { id: 'email', icon: <Mail />, label: 'Rispondere ad una mail' },
  { id: 'lead', icon: <UserPlus />, label: 'Impostare un primo contatto con un lead' },
  { id: 'social', icon: <Share2 />, label: 'Creare un contenuto social' },
  { id: 'summary', icon: <FileText />, label: 'Riassumere una comunicazione' }
];

const secondaryGoals = {
  info: [
    'Informazioni su un veicolo specifico',
    'Confronto tra veicoli',
    'Disponibilità e prezzi',
    'Caratteristiche tecniche'
  ],
  email: [
    'Risposta a richiesta preventivo',
    'Follow-up dopo test drive',
    'Gestione reclamo',
    'Conferma appuntamento'
  ],
  lead: [
    'Primo contatto dopo richiesta online',
    'Qualificazione lead',
    'Invito test drive',
    'Presentazione offerta'
  ],
  social: [
    'Post nuovo arrivo veicolo',
    'Promozione servizi',
    'Storia di successo cliente',
    'Evento showroom'
  ],
  summary: [
    'Sintesi conversazione cliente',
    'Riepilogo trattativa',
    'Report attività settimanale',
    'Analisi feedback clienti'
  ]
};

// Create EmbeddedCarResult component for car search
const EmbeddedCarResult = ({ car, onAddCar, darkMode }) => {
  return (
    <Card 
      variant="outlined"
      sx={{ 
        mb: 2.5, 
        cursor: 'pointer',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: 3
        },
        bgcolor: darkMode ? 'rgba(66, 66, 66, 0.5)' : 'background.paper'
      }}
      onClick={() => onAddCar(car)}
    >
      <CardContent sx={{ py: 2, px: 3, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {car.brand} {car.model}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {car.variant || 'N/A'} - {car.registrationyear || 'Anno N/D'}
            </Typography>
            {car.saleprice && (
              <Typography variant="subtitle1" sx={{ color: 'primary.main', fontWeight: 'bold', mt: 1 }}>
                {car.saleprice.toLocaleString()}€
              </Typography>
            )}
          </Box>
          <IconButton 
            size="large" 
            color="primary"
            sx={{ 
              bgcolor: 'primary.main', 
              color: 'white',
              width: 48,
              height: 48,
              '&:hover': {
                bgcolor: 'primary.dark'
              }
            }}
          >
            <Plus size={24} />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};

// Create EmbeddedClientResult component for client search
const EmbeddedClientResult = ({ client, onAddClient, darkMode }) => {
  return (
    <Card 
      variant="outlined"
      sx={{ 
        mb: 2.5, 
        cursor: 'pointer',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: 3
        },
        bgcolor: darkMode ? 'rgba(66, 66, 66, 0.5)' : 'background.paper'
      }}
      onClick={() => onAddClient(client)}
    >
      <CardContent sx={{ py: 2, px: 3, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {client.name}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {client.email || 'Email N/D'}
            </Typography>
            {client.phone && (
              <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                {client.phone}
              </Typography>
            )}
          </Box>
          <IconButton 
            size="large" 
            color="primary"
            sx={{ 
              bgcolor: 'primary.main', 
              color: 'white',
              width: 48,
              height: 48,
              '&:hover': {
                bgcolor: 'primary.dark'
              }
            }}
          >
            <Plus size={24} />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};

// Helper function to generate prompt based on selections
const generatePromptText = (primaryGoal, secondaryGoal, selectedCars, selectedClients) => {
  // Find the label for the primary goal
  const primaryGoalLabel = primaryGoals.find(goal => goal.id === primaryGoal)?.label || '';
  
  let promptText = `Sei un assistente per un concessionario auto, e il tuo obiettivo è: ${primaryGoalLabel} - ${secondaryGoal}.\n\n`;
  
  // Add cars information
  if (selectedCars && selectedCars.length > 0) {
    promptText += "Informazioni sui veicoli:\n";
    selectedCars.forEach((car, index) => {
      promptText += `${index + 1}. ${car.brand} ${car.model} (${car.registrationyear}) - ${car.saleprice?.toLocaleString()}€\n`;
      promptText += `   - Km: ${car.km?.toLocaleString()}, Alimentazione: ${car.fuel || 'N/A'}\n`;
      if (car.bodystyle || car.transmission || car.colour) {
        promptText += `   - Dettagli: ${car.bodystyle || 'N/A'}, ${car.transmission || 'N/A'}, ${car.colour || 'N/A'}\n`;
      }
    });
    promptText += '\n';
  }
  
  // Add clients information
  if (selectedClients && selectedClients.length > 0) {
    promptText += "Informazioni sui clienti:\n";
    selectedClients.forEach((client, index) => {
      promptText += `${index + 1}. ${client.name}\n`;
      if (client.email) promptText += `   - Email: ${client.email}\n`;
      if (client.number) promptText += `   - Telefono: ${client.number}\n`;
      if (client.last_contact) promptText += `   - Ultimo contatto: ${new Date(client.last_contact).toLocaleDateString()}\n`;
    });
    promptText += '\n';
  }
  
  // Add specific instructions based on the goal
  switch(primaryGoal) {
    case 'info':
      promptText += "Fornisci informazioni chiare e dettagliate, evidenziando i punti di forza.";
      break;
    case 'email':
      promptText += "Scrivi una email professionale ma cordiale, includendo tutte le informazioni necessarie.";
      break;
    case 'lead':
      promptText += "Utilizza un approccio personalizzato e convincente per catturare l'interesse del potenziale cliente.";
      break;
    case 'social':
      promptText += "Crea un contenuto coinvolgente, breve ma efficace, adatto ai social media.";
      break;
    case 'summary':
      promptText += "Sintetizza le informazioni in modo chiaro e strutturato, evidenziando i punti chiave.";
      break;
    default:
      break;
  }
  
  //console.log('Generated prompt text:', promptText);
  //console.log('With data:', {
  //  primaryGoal,
  //  primaryGoalLabel,
  //  secondaryGoal,
  //  selectedCars: selectedCars?.length,
  //  selectedClients: selectedClients?.length
  //});
  
  return promptText;
};

const PromptStructureDialog = ({ 
  open, 
  onClose, 
  onGoalsSelected,
  selectedCars = [],
  selectedClients = [],
  onRemoveCar,
  onRemoveClient
}) => {
  const [selectedPrimaryGoal, setSelectedPrimaryGoal] = useState('');
  const [selectedSecondaryGoal, setSelectedSecondaryGoal] = useState('');
  const [showPromptPreview, setShowPromptPreview] = useState(false);
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [promptCopied, setPromptCopied] = useState(false);
  
  // Add local state for cars and clients that we'll manage internally
  const [localSelectedCars, setLocalSelectedCars] = useState([]);
  const [localSelectedClients, setLocalSelectedClients] = useState([]);
  
  // Embedded search states
  const [showCarSearch, setShowCarSearch] = useState(false);
  const [showClientSearch, setShowClientSearch] = useState(false);
  const [carSearchTerm, setCarSearchTerm] = useState('');
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [carSearchResults, setCarSearchResults] = useState([]);
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [isCarSearching, setIsCarSearching] = useState(false);
  const [isClientSearching, setIsClientSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [darkMode, setDarkMode] = useState(false);

  // Sync local state with props when props change
  useEffect(() => {
    setLocalSelectedCars(selectedCars || []);
  }, [selectedCars]);

  useEffect(() => {
    setLocalSelectedClients(selectedClients || []);
  }, [selectedClients]);

  // Detect dark mode from body class or localStorage
  useEffect(() => {
    const isDarkMode = document.body.classList.contains('dark-mode') || 
                       localStorage.getItem('darkMode') === 'true';
    setDarkMode(isDarkMode);
  }, []);

  useEffect(() => {
    if (selectedPrimaryGoal && selectedSecondaryGoal) {
      const newPrompt = generatePromptText(
        selectedPrimaryGoal, 
        selectedSecondaryGoal, 
        localSelectedCars, 
        localSelectedClients
      );
      setGeneratedPrompt(newPrompt);
    }
  }, [selectedPrimaryGoal, selectedSecondaryGoal, localSelectedCars, localSelectedClients]);

  const handlePrimaryGoalChange = (event) => {
    setSelectedPrimaryGoal(event.target.value);
    setSelectedSecondaryGoal('');
  };

  const handleSecondaryGoalChange = (event) => {
    setSelectedSecondaryGoal(event.target.value);
    setShowPromptPreview(true);
  };

  const handleConfirm = () => {
    if (selectedPrimaryGoal && selectedSecondaryGoal) {
      // Create the structured prompt data
      const promptData = {
        primaryGoal: selectedPrimaryGoal,
        secondaryGoal: selectedSecondaryGoal,
        selectedCars: localSelectedCars,
        selectedClients: localSelectedClients,
        promptText: generatedPrompt // Include the generated prompt text
      };

      //console.log('Confirming selection with promptData:', JSON.stringify(promptData));

      // Send to parent component
      onGoalsSelected(promptData);
      onClose();
    }
  };

  const copyPromptToClipboard = () => {
    navigator.clipboard.writeText(generatedPrompt);
    setPromptCopied(true);
    setTimeout(() => setPromptCopied(false), 3000);
  };

  // Search functions
  const searchCars = async (term) => {
    if (!term.trim()) {
      setCarSearchResults([]);
      return;
    }
    
    setIsCarSearching(true);
    setSearchError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error("Authentication token not found");
      }
      
      const response = await axios.get(`${API_BASE_URL}/carstocks/`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          search: term,
          page_size: 5
        }
      });
      
      if (response.data && response.data.results) {
        setCarSearchResults(response.data.results || []);
      } else {
        setCarSearchResults([]);
        console.warn("Car search response didn't contain expected data structure", response.data);
      }
    } catch (error) {
      console.error("Error searching cars:", error);
      setSearchError(error.response?.data?.detail || error.message || "Failed to search cars. Please try again.");
      setCarSearchResults([]);
    } finally {
      setIsCarSearching(false);
    }
  };

  const searchClients = async (term) => {
    if (!term.trim()) {
      setClientSearchResults([]);
      return;
    }
    
    setIsClientSearching(true);
    setSearchError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error("Authentication token not found");
      }
      
      const response = await axios.get(`${CRM_API_URL}/Clients/`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          search: term,
          page_size: 5
        }
      });
      
      if (response.data && response.data.results) {
        const clients = response.data.results || [];
        setClientSearchResults(clients.map(client => ({
          id: client.id,
          name: `${client.first_name} ${client.last_name}`.trim(),
          email: client.email,
          phone: client.number,
          last_contact: client.last_contact,
          first_name: client.first_name,
          last_name: client.last_name,
          number: client.number
        })));
      } else {
        setClientSearchResults([]);
        console.warn("Client search response didn't contain expected data structure", response.data);
      }
    } catch (error) {
      console.error("Error searching clients:", error);
      setSearchError(error.response?.data?.detail || error.message || "Failed to search clients. Please try again.");
      setClientSearchResults([]);
    } finally {
      setIsClientSearching(false);
    }
  };

  // Handle search input changes with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (carSearchTerm) searchCars(carSearchTerm);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [carSearchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (clientSearchTerm) searchClients(clientSearchTerm);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [clientSearchTerm]);

  // Update handleAddCarFromSearch to properly handle adding cars
  const handleAddCarFromSearch = (car) => {
    if (!localSelectedCars.some(c => c.vehicleid === car.vehicleid)) {
      // Create a copy to avoid reference issues
      const carToAdd = { ...car };
      
      // Update the local state
      const updatedCars = [...localSelectedCars, carToAdd];
      setLocalSelectedCars(updatedCars);
    }
    
    // Toggle search panel
    setShowCarSearch(false);
    setCarSearchTerm('');
  };

  const handleAddClientFromSearch = (client) => {
    if (!localSelectedClients.some(c => c.id === client.id)) {
      // Create a copy to avoid reference issues
      const clientToAdd = { ...client };
      
      // Update the local state
      const updatedClients = [...localSelectedClients, clientToAdd];
      setLocalSelectedClients(updatedClients);
    }
    
    // Toggle search panel
    setShowClientSearch(false);
    setClientSearchTerm('');
  };

  // Handle local removal of cars and clients
  const handleRemoveCar = (car) => {
    const updatedCars = localSelectedCars.filter(c => 
      c.vehicleid !== car.vehicleid && c.id !== car.id
    );
    setLocalSelectedCars(updatedCars);
    
    // Also call the parent's removal function if available
    if (onRemoveCar) {
      onRemoveCar(car);
    }
  };

  const handleRemoveClient = (client) => {
    const updatedClients = localSelectedClients.filter(c => c.id !== client.id);
    setLocalSelectedClients(updatedClients);
    
    // Also call the parent's removal function if available
    if (onRemoveClient) {
      onRemoveClient(client);
    }
  };

  // Reset search when dialog closes
  useEffect(() => {
    if (!open) {
      setShowCarSearch(false);
      setShowClientSearch(false);
      setCarSearchTerm('');
      setClientSearchTerm('');
    }
  }, [open]);

  // Improve search panels by making them appear as modal overlays over the main dialog
  const renderCarSearchPanel = () => {
    if (!showCarSearch) return null;
    
    return (
      <Box sx={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: 'rgba(0, 0, 0, 0.75)',
        zIndex: 9999,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2
      }}>
        <Paper 
          elevation={24} 
          sx={{ 
            width: '90%', 
            maxWidth: 600,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            height: '80vh',
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          <Box sx={{ 
            p: 3,
            borderBottom: '1px solid',
            borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
            display: 'flex',
            alignItems: 'center',
          }}>
            <IconButton 
              onClick={() => setShowCarSearch(false)} 
              sx={{ mr: 2 }}
              aria-label="Torna indietro"
            >
              <ArrowLeft size={24} />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>Cerca Veicoli</Typography>
          </Box>
          
          <Box sx={{ p: 3, pt: 2 }}>
            <TextField
              fullWidth
              size="medium"
              placeholder="Cerca per marca, modello, anno..."
              value={carSearchTerm}
              onChange={(e) => setCarSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={20} />
                  </InputAdornment>
                ),
                endAdornment: isCarSearching ? (
                  <InputAdornment position="end">
                    <CircularProgress size={24} />
                  </InputAdornment>
                ) : carSearchTerm ? (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setCarSearchTerm('')}>
                      <X size={18} />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
              sx={{ mb: 3 }}
            />
          </Box>
          
          {searchError && (
            <Alert severity="error" sx={{ mx: 3, mb: 2 }}>{searchError}</Alert>
          )}
          
          <Box sx={{ 
            flex: 1,
            overflowY: 'auto',
            px: 3,
            pb: 3
          }}>
            {carSearchResults.length > 0 ? (
              carSearchResults.map(car => (
                <EmbeddedCarResult 
                  key={car.vehicleid} 
                  car={car} 
                  onAddCar={handleAddCarFromSearch}
                  darkMode={darkMode}
                />
              ))
            ) : carSearchTerm && !isCarSearching ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '200px' }}>
                <Typography variant="body1" color="text.secondary">
                  Nessun risultato trovato
                </Typography>
              </Box>
            ) : !isCarSearching && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '200px' }}>
                <Typography variant="body1" color="text.secondary">
                  Inizia a digitare per cercare veicoli
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    );
  };

  const renderClientSearchPanel = () => {
    if (!showClientSearch) return null;
    
    return (
      <Box sx={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: 'rgba(0, 0, 0, 0.75)',
        zIndex: 9999,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2
      }}>
        <Paper 
          elevation={24} 
          sx={{ 
            width: '90%', 
            maxWidth: 600,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            height: '80vh',
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          <Box sx={{ 
            p: 3,
            borderBottom: '1px solid',
            borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
            display: 'flex',
            alignItems: 'center',
          }}>
            <IconButton 
              onClick={() => setShowClientSearch(false)} 
              sx={{ mr: 2 }}
              aria-label="Torna indietro"
            >
              <ArrowLeft size={24} />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>Cerca Clienti</Typography>
          </Box>
          
          <Box sx={{ p: 3, pt: 2 }}>
            <TextField
              fullWidth
              size="medium"
              placeholder="Cerca per nome, email, telefono..."
              value={clientSearchTerm}
              onChange={(e) => setClientSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={20} />
                  </InputAdornment>
                ),
                endAdornment: isClientSearching ? (
                  <InputAdornment position="end">
                    <CircularProgress size={24} />
                  </InputAdornment>
                ) : clientSearchTerm ? (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setClientSearchTerm('')}>
                      <X size={18} />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
              sx={{ mb: 3 }}
            />
          </Box>
          
          {searchError && (
            <Alert severity="error" sx={{ mx: 3, mb: 2 }}>{searchError}</Alert>
          )}
          
          <Box sx={{ 
            flex: 1,
            overflowY: 'auto',
            px: 3,
            pb: 3
          }}>
            {clientSearchResults.length > 0 ? (
              clientSearchResults.map(client => (
                <EmbeddedClientResult 
                  key={client.id} 
                  client={client} 
                  onAddClient={handleAddClientFromSearch}
                  darkMode={darkMode}
                />
              ))
            ) : clientSearchTerm && !isClientSearching ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '200px' }}>
                <Typography variant="body1" color="text.secondary">
                  Nessun risultato trovato
                </Typography>
              </Box>
            ) : !isClientSearching && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '200px' }}>
                <Typography variant="body1" color="text.secondary">
                  Inizia a digitare per cercare clienti
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Typography variant="h6">Seleziona l'obiettivo della conversazione</Typography>
      </DialogTitle>
      
      <DialogContent dividers sx={{ position: 'relative' }}>
        {/* Main content */}
        {!showCarSearch && !showClientSearch ? (
          <>
            {/* Knowledge Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>Conoscenza Selezionata</Typography>
              
              {/* Cars Knowledge */}
              <Paper variant="outlined" sx={{ mb: 2, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Car size={18} />
                    Veicoli {localSelectedCars.length > 0 && `(${localSelectedCars.length})`}
                  </Typography>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    onClick={() => setShowCarSearch(true)}
                    startIcon={<Plus size={16} />}
                    sx={{ textTransform: 'none' }}
                  >
                    Aggiungi veicolo
                  </Button>
                </Box>
                
                {localSelectedCars.length > 0 ? (
                  <List dense>
                    {localSelectedCars.map((car) => (
                      <ListItem key={car.vehicleid || car.id} sx={{ py: 0.5 }}>
                        <ListItemText 
                          primary={`${car.brand} ${car.model} (${car.registrationyear})`}
                          secondary={`${car.saleprice?.toLocaleString()}€`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton 
                            edge="end" 
                            size="small" 
                            onClick={() => handleRemoveCar(car)}
                            sx={{ color: 'error.main' }}
                          >
                            <X size={16} />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
                    Nessun veicolo selezionato
                  </Typography>
                )}
              </Paper>

              {/* Clients Knowledge */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Users size={18} />
                    Clienti {localSelectedClients.length > 0 && `(${localSelectedClients.length})`}
                  </Typography>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    onClick={() => setShowClientSearch(true)}
                    startIcon={<Plus size={16} />}
                    sx={{ textTransform: 'none' }}
                  >
                    Aggiungi cliente
                  </Button>
                </Box>
                
                {localSelectedClients.length > 0 ? (
                  <List dense>
                    {localSelectedClients.map((client) => (
                      <ListItem key={client.id} sx={{ py: 0.5 }}>
                        <ListItemText 
                          primary={client.name}
                          secondary={client.email}
                        />
                        <ListItemSecondaryAction>
                          <IconButton 
                            edge="end" 
                            size="small" 
                            onClick={() => handleRemoveClient(client)}
                            sx={{ color: 'error.main' }}
                          >
                            <X size={16} />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
                    Nessun cliente selezionato
                  </Typography>
                )}
              </Paper>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Goals Section */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>Obiettivo Primario</Typography>
              <RadioGroup value={selectedPrimaryGoal} onChange={handlePrimaryGoalChange}>
                {primaryGoals.map((goal) => (
                  <FormControlLabel
                    key={goal.id}
                    value={goal.id}
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {goal.icon}
                        <Typography>{goal.label}</Typography>
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>

              <Collapse in={!!selectedPrimaryGoal}>
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>Obiettivo Secondario</Typography>
                  <RadioGroup value={selectedSecondaryGoal} onChange={handleSecondaryGoalChange}>
                    {selectedPrimaryGoal && secondaryGoals[selectedPrimaryGoal].map((goal, index) => (
                      <FormControlLabel
                        key={index}
                        value={goal}
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ChevronRight size={16} />
                            <Typography>{goal}</Typography>
                          </Box>
                        }
                      />
                    ))}
                  </RadioGroup>
                </Box>
              </Collapse>
            </Box>

            {/* Prompt Preview */}
            {showPromptPreview && selectedSecondaryGoal && (
              <Box sx={{ mt: 3 }}>
                <Accordion>
                  <AccordionSummary
                    expandIcon={<ChevronDown size={20} />}
                    sx={{ bgcolor: 'action.hover' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FileCode size={18} />
                      <Typography>Anteprima del Prompt</Typography>
                      <Chip 
                        label="Creato" 
                        size="small" 
                        color="success" 
                        variant="outlined" 
                        icon={<Check size={14} />}
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box 
                      sx={{ 
                        bgcolor: 'background.paper', 
                        p: 2, 
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        position: 'relative',
                        whiteSpace: 'pre-line',
                        fontSize: '0.875rem',
                        fontFamily: 'monospace'
                      }}
                    >
                      {generatedPrompt}
                      <IconButton 
                        size="small"
                        onClick={copyPromptToClipboard}
                        sx={{ 
                          position: 'absolute', 
                          top: 8, 
                          right: 8,
                          bgcolor: 'background.paper',
                          '&:hover': {
                            bgcolor: 'action.hover'
                          }
                        }}
                      >
                        <FileCode size={16} />
                      </IconButton>
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                      Questo prompt sarà inviato all'IA per guidare la conversazione.
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}
          </>
        ) : null}
        
        {/* Render search panels as overlays */}
        {renderCarSearchPanel()}
        {renderClientSearchPanel()}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Annulla
        </Button>
        {!showCarSearch && !showClientSearch && (
          <Button 
            onClick={handleConfirm}
            color="primary"
            disabled={!selectedPrimaryGoal || !selectedSecondaryGoal}
            variant="contained"
          >
            Conferma
          </Button>
        )}
      </DialogActions>

      <Snackbar
        open={promptCopied}
        autoHideDuration={3000}
        onClose={() => setPromptCopied(false)}
        message="Prompt copiato negli appunti"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Dialog>
  );
};

export default PromptStructureDialog;